import { handleActions } from 'redux-actions'
import { selectorForSlice, setState } from 'lp-redux-utils'
import { createSelector } from 'reselect'
import * as LS from 'local-storage'
import * as apiActions from 'api-actions'
import { setOnSuccess } from 'lp-redux-api'
import { isAdmin, isAuthenticated } from './auth'
import * as actions from './global-actions'
import * as Types from 'types'
import { set, serializeLocation, without } from 'utils'
import { years } from 'select-options'
import { update, sortBy } from 'lodash/fp'
import { orderBy, toNumber } from 'lodash'

const reducerKey = 'global'
const slice = 'root.artistPortal.global'

const initialState = {
  currentArtistId: LS.getCurrentArtistId(),
  isAdmin: isAdmin(),
  isAuthenticated: isAuthenticated(),
}

const reducer = handleActions(
  {
    [actions.clearArtist]: () => initialState,
    [actions.setCurrentArtistId]: (state, { payload: id }) => {
      LS.clearCurrentArtistId()
      if (!id) return set('currentArtistId', null, state)
      LS.setCurrentArtistId(id)
      return set('currentArtistId', toNumber(id), state)
    },
    [actions.setAuthenticated]: (
      state,
      { payload: { token, persistSession } }
    ) => {
      LS.clearAllAuthTokens()
      if (!token) return set('isAuthenticated', false, state)
      LS.setArtistAuthToken(token, persistSession)
      return set('isAuthenticated', true, state)
    },
    [actions.setIsAdmin]: (state, { payload: isAdmin }) => {
      LS.clearIsAdmin()
      if (!isAdmin) return set('isAdmin', false, state)
      LS.setIsAdmin(true)
      return set('isAdmin', true, state)
    },
    [actions.setArtist]: setState('artist'),
    [actions.setPrimaryContact]: setState('artist.contact'),
    [apiActions.fetchArtistAuthorized]: setOnSuccess('artist'),
    [apiActions.fetchPartnerOptions]: setOnSuccess('partnerOptions'),
    [apiActions.fetchCurrentArtistUserDetails]: setOnSuccess('userDetails'),
    [apiActions.fetchSchoolOptions]: setOnSuccess('schoolOptions', (action) => {
      return action.payload.data.activeSchools
    }),
    [apiActions.fetchActiveSchoolYears]: setOnSuccess('activeSchoolYears'),
    [actions.setUserDetails]: setState('userDetails'),
    [apiActions.agreeToTerms]: setOnSuccess(
      'userDetails.agreedAt',
      ({ payload: { data } }) => data.agreedAt
    ),
    [apiActions.fetchNotifications]: setOnSuccess('notifications'),
    [apiActions.markAllAsRead]: setOnSuccess('notifications'),
    [apiActions.deleteNotification]: setOnSuccess('notifications'),
    [apiActions.markAsRead]: setOnSuccess('notifications'),
    [apiActions.fetchNotificationPreferences]: setOnSuccess(
      'notificationPreferences'
    ),
    [apiActions.updateNotificationPreferences]: setOnSuccess(
      'notificationPreferences'
    ),
  },
  initialState
)

const select = selectorForSlice(slice)

const selectors = {
  activeSchoolYears: select('activeSchoolYears'),
  currentArtistId: select('currentArtistId'),
  isAdmin: select('isAdmin'),
  isAuthenticated: select('isAuthenticated'),
  unsortedArtist: select('artist'),
  schoolOptions: select('schoolOptions'),
  selectOptions: select('selectOptions'),
  userDetails: select('userDetails'),
  notifications: select('notifications'),
  notificationPreferences: select('notificationPreferences'),
  partnerOptions: select('partnerOptions'),
}

// Computed

selectors.artist = createSelector(
  [selectors.unsortedArtist],
  function (unsortedArtist) {
    if (!unsortedArtist) return null
    return sortArtist(unsortedArtist)
  }
)

selectors.organizations = createSelector([selectors.artist], function (artist) {
  if (!artist) return []
  return artist.organizations
})

selectors.currentSchoolYear = createSelector(
  [selectors.artist],
  function (artist) {
    if (!artist) return null
    return artist.detail.schoolYearId
  }
)

selectors.organizationLocations = createSelector(
  [selectors.organizations],
  function (organizations) {
    return organizations.map(serializeLocation(Types.PARTNER_TYPE))
  }
)

selectors.currentArtistLocation = createSelector(
  [selectors.artist],
  function (artist) {
    if (!artist) return null
    return serializeLocation(Types.ARTIST_TYPE, artist)
  }
)

selectors.allLocations = createSelector(
  [selectors.organizationLocations, selectors.currentArtistLocation],
  function (organizationLocations, currentArtistLocation) {
    return [...organizationLocations, currentArtistLocation]
  }
)

selectors.reportYears = createSelector(
  [selectors.currentSchoolYear],
  function (currentSchoolYear) {
    return without(years, currentSchoolYear)
  }
)

// Order school years by most recent (DESC)
selectors.orderedActiveSchoolYears = createSelector(
  [selectors.activeSchoolYears],
  function (schoolYears) {
    if (!schoolYears) return
    return orderBy(schoolYears, 'number', 'desc')
  }
)

// Helpers
function sortArtist(artist) {
  return update('organizations', sortBy('name'), artist)
}

export { reducer, reducerKey, selectors }
